import { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { CalendarClock, Plus, Trash2, CheckCircle2, XCircle, Edit3, Sparkles, Mail, Zap } from 'lucide-react';

const CARD = { backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', border: '1px solid #EFEAE0' };
const CATEGORIES = [
  { value: 'dating-engagement', label: 'Dating & Engagement' },
  { value: 'marriage', label: 'Marriage' },
  { value: 'family', label: 'Family' },
  { value: 'friendships', label: 'Friendships' },
  { value: 'personal-struggles', label: 'Personal Struggles' },
  { value: 'other', label: 'Other' },
];

function StatusBadge({ status, planned_date }) {
  const now = new Date();
  const pd = new Date(planned_date);
  const isOverdue = status === 'planned' && pd < now;
  const map = {
    planned: isOverdue
      ? { bg: '#DC262620', color: '#DC2626', label: `Overdue` }
      : { bg: '#5B7C9920', color: '#5B7C99', label: 'Planned' },
    posted: { bg: '#78A18E20', color: '#78A18E', label: 'Posted' },
    skipped: { bg: '#8C867D20', color: '#8C867D', label: 'Skipped' },
  };
  const s = map[status] || map.planned;
  return (
    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold" style={{ backgroundColor: s.bg, color: s.color, fontFamily: 'Nunito, sans-serif' }}>
      {s.label}
    </span>
  );
}

function toDatetimeLocal(dt) {
  const d = new Date(dt);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ContentCalendarTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [rhythmWeeks, setRhythmWeeks] = useState(4);
  const [sendingTest, setSendingTest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/content-plans');
      setPlans(data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const generateRhythm = async () => {
    if (!window.confirm(`Generate ${rhythmWeeks} weeks of Mon/Wed/Fri posting placeholders (${rhythmWeeks * 3} plans)? You can edit each title later.`)) return;
    setGenerating(true);
    try {
      const { data } = await api.post('/admin/content-plans/rhythm', { weeks: rhythmWeeks });
      toast.success(`Created ${data.created} plans across ${data.weeks} weeks`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate rhythm');
    } finally { setGenerating(false); }
  };

  const sendTestReminder = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post('/admin/content-plans/test-reminder');
      if (data.ok) toast.success(`Test reminder sent — check your admin inbox`);
      else toast.error('Reminder returned an error (check email settings)');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send test');
    } finally { setSendingTest(false); }
  };

  const openNew = () => {
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(10, 0, 0, 0);
    setEditing({ title: '', category: 'marriage', planned_date: toDatetimeLocal(tomorrow), notes: '' });
  };
  const openEdit = (p) => {
    setEditing({ id: p.id, title: p.title, category: p.category, planned_date: toDatetimeLocal(p.planned_date), notes: p.notes || '' });
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error('A title is required'); return; }
    if (!editing.planned_date) { toast.error('Pick a planned date'); return; }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        category: editing.category,
        planned_date: new Date(editing.planned_date).toISOString(),
        notes: editing.notes || '',
      };
      if (editing.id) {
        await api.put(`/admin/content-plans/${editing.id}`, payload);
        toast.success('Plan updated');
      } else {
        await api.post('/admin/content-plans', payload);
        toast.success('Plan added');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/content-plans/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch { toast.error('Could not update status'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}" from your calendar?`)) return;
    try { await api.delete(`/admin/content-plans/${p.id}`); toast.success('Removed'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const upcoming = plans.filter(p => p.status === 'planned' && new Date(p.planned_date) >= new Date());
  const overdue = plans.filter(p => p.status === 'planned' && new Date(p.planned_date) < new Date());
  const done = plans.filter(p => p.status !== 'planned');

  return (
    <div className="space-y-6" data-testid="content-calendar-tab">
      {/* Intro / new-plan bar */}
      <div className="p-5 rounded-3xl relative overflow-hidden" style={{ ...CARD, background: 'linear-gradient(135deg, #FDFBF7 0%, #FFFFFF 100%)' }}>
        <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
          <div className="p-2.5 rounded-xl flex-shrink-0" style={{ backgroundColor: '#5B7C9920' }}>
            <CalendarClock className="w-5 h-5" style={{ color: '#5B7C99' }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Content Calendar</h3>
            <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
              Sketch out what you want to write and when. You'll get a warm reminder email <strong>the day before</strong> and <strong>the morning of</strong> each planned post so nothing slips.
            </p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full transition hover:-translate-y-0.5 flex-shrink-0"
            style={{ backgroundColor: '#5B7C99', color: '#FDFBF7', fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: 13 }}
            data-testid="new-plan-btn"
          >
            <Plus className="w-4 h-4" /> New plan
          </button>
        </div>
      </div>

      {/* Suggested Rhythm strategy card */}
      <div className="p-6 rounded-3xl relative overflow-hidden" style={{ ...CARD, background: 'linear-gradient(135deg, #FFF8ED 0%, #FFFFFF 100%)' }}>
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10" style={{ backgroundColor: '#E57A44' }} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5" style={{ color: '#E57A44' }} />
            <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Your Suggested Rhythm</h3>
          </div>
          <p className="text-sm mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
            Faith-based content sites see the strongest engagement with <strong>3 posts a week</strong> — enough for readers to build a habit, gentle enough not to burn you out. Here's the rhythm I recommend:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <RhythmDay day="Monday" tint="#78A18E" title="Encouragement" hint="Kick off the week strong with a hopeful, grounding piece. Refresh the Verse of the Week too." />
            <RhythmDay day="Wednesday" tint="#5B7C99" title="Reflection" hint="Something deeper for readers halfway through the week — marriage, family, honest struggles." />
            <RhythmDay day="Friday" tint="#E57A44" title="Hope" hint="Lighter, testimony-style. Someone else's restoration story. Sends readers into the weekend uplifted." />
          </div>

          <div className="flex items-center gap-3 flex-wrap p-3 rounded-2xl" style={{ backgroundColor: '#FDFBF7' }}>
            <span className="text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>Generate</span>
            <select
              value={rhythmWeeks}
              onChange={(e) => setRhythmWeeks(parseInt(e.target.value, 10))}
              className="px-3 py-1.5 rounded-full border text-sm"
              style={{ fontFamily: 'Nunito, sans-serif', borderColor: '#E6E2D8', backgroundColor: '#FFFFFF', color: '#2C2A29' }}
              data-testid="rhythm-weeks-select"
            >
              {[1, 2, 4, 8, 12].map(n => <option key={n} value={n}>{n} week{n === 1 ? '' : 's'}</option>)}
            </select>
            <span className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>of Mon/Wed/Fri placeholder posts</span>
            <button
              onClick={generateRhythm}
              disabled={generating}
              className="ml-auto flex items-center gap-2 px-4 py-2 rounded-full transition hover:-translate-y-0.5"
              style={{ backgroundColor: '#E57A44', color: '#FDFBF7', fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: 13, opacity: generating ? 0.6 : 1 }}
              data-testid="rhythm-generate-btn"
            >
              <Sparkles className="w-4 h-4" /> {generating ? 'Generating…' : 'Set up my rhythm'}
            </button>
          </div>

          <p className="text-xs mt-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
            Placeholders start from next Monday. You can rename each one and adjust dates anytime.
          </p>
        </div>
      </div>

      {/* Reminder emails banner */}
      <div className="p-4 rounded-2xl flex items-center gap-3 flex-wrap" style={{ backgroundColor: '#F0F5FA', border: '1px solid #5B7C9925' }}>
        <div className="p-2 rounded-xl flex-shrink-0" style={{ backgroundColor: '#5B7C9925' }}>
          <Mail className="w-4 h-4" style={{ color: '#5B7C99' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>Auto-reminders are ON</p>
          <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
            You'll get an email <strong>the evening before</strong> (heads-up), <strong>morning of</strong> (day-of nudge), and <strong>every 3 days</strong> if anything goes overdue.
          </p>
        </div>
        <button
          onClick={sendTestReminder}
          disabled={sendingTest || plans.length === 0}
          className="px-3 py-1.5 rounded-full text-xs font-semibold flex-shrink-0"
          style={{ backgroundColor: '#FFFFFF', color: '#5B7C99', border: '1px solid #5B7C9940', fontFamily: 'Nunito, sans-serif', opacity: (sendingTest || plans.length === 0) ? 0.5 : 1 }}
          data-testid="test-reminder-btn"
        >
          {sendingTest ? 'Sending…' : 'Send a test reminder'}
        </button>
      </div>

      {loading ? (
        <div className="p-12 rounded-3xl text-center" style={CARD}>
          <p style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Loading calendar…</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="p-12 rounded-3xl text-center" style={CARD}>
          <CalendarClock className="w-10 h-10 mx-auto mb-3" style={{ color: '#C9C3B4' }} />
          <p style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: '18px', fontWeight: 700 }}>Nothing planned yet</p>
          <p className="mt-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
            Tap <strong>New plan</strong> to sketch your next article. Even one title and a date works — you can flesh it out later.
          </p>
        </div>
      ) : (
        <>
          {overdue.length > 0 && (
            <Section title="Overdue" subtitle="These were planned for the past and haven't been posted yet." tint="#DC2626" plans={overdue} openEdit={openEdit} setStatus={setStatus} remove={remove} />
          )}
          {upcoming.length > 0 && (
            <Section title="Coming up" subtitle="Your next writing rhythm." tint="#5B7C99" plans={upcoming} openEdit={openEdit} setStatus={setStatus} remove={remove} />
          )}
          {done.length > 0 && (
            <Section title="Wrapped" subtitle="Posts you've marked complete or skipped." tint="#78A18E" plans={done} openEdit={openEdit} setStatus={setStatus} remove={remove} />
          )}
        </>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(44,42,41,0.5)' }} onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-3xl p-6 space-y-4" style={{ backgroundColor: '#FFFFFF' }}>
            <h3 className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
              {editing.id ? 'Edit plan' : 'New plan'}
            </h3>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>Article title</label>
              <input
                type="text"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="E.g. Rebuilding trust after betrayal"
                className="w-full px-4 py-2.5 rounded-xl border"
                style={{ fontFamily: 'Nunito, sans-serif', borderColor: '#E6E2D8', backgroundColor: '#FDFBF7', color: '#2C2A29' }}
                data-testid="plan-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>Category</label>
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border"
                  style={{ fontFamily: 'Nunito, sans-serif', borderColor: '#E6E2D8', backgroundColor: '#FDFBF7', color: '#2C2A29' }}
                  data-testid="plan-category-select"
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>Planned date</label>
                <input
                  type="datetime-local"
                  value={editing.planned_date}
                  onChange={(e) => setEditing({ ...editing, planned_date: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl border"
                  style={{ fontFamily: 'Nunito, sans-serif', borderColor: '#E6E2D8', backgroundColor: '#FDFBF7', color: '#2C2A29' }}
                  data-testid="plan-date-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#2C2A29' }}>Notes (optional)</label>
              <textarea
                value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                rows={3}
                placeholder="Scriptures to include, hooks, personal stories to weave in…"
                className="w-full px-4 py-2.5 rounded-xl border"
                style={{ fontFamily: 'Nunito, sans-serif', borderColor: '#E6E2D8', backgroundColor: '#FDFBF7', color: '#2C2A29' }}
                data-testid="plan-notes-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#FDFBF7', color: '#595553', border: '1px solid #E6E2D8' }}>
                Cancel
              </button>
              <button onClick={save} disabled={saving} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ backgroundColor: '#5B7C99', color: '#FDFBF7', opacity: saving ? 0.6 : 1 }} data-testid="plan-save-btn">
                {saving ? 'Saving…' : 'Save plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, tint, plans, openEdit, setStatus, remove }) {
  return (
    <div className="p-6 rounded-3xl" style={CARD}>
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tint }} />
          <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{title} <span style={{ color: '#8C867D', fontWeight: 400 }}>({plans.length})</span></h3>
        </div>
        <p className="text-xs mt-0.5" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>{subtitle}</p>
      </div>
      <div className="space-y-2">
        {plans.map(p => (
          <div key={p.id} className="p-4 rounded-2xl flex items-start gap-3 flex-wrap sm:flex-nowrap" style={{ backgroundColor: '#FDFBF7' }} data-testid={`plan-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{p.title}</h4>
                <StatusBadge status={p.status} planned_date={p.planned_date} />
              </div>
              <p className="text-xs mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                {new Date(p.planned_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {p.category}
              </p>
              {p.notes && (
                <p className="text-sm mt-2 whitespace-pre-wrap" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{p.notes}</p>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {p.status === 'planned' && (
                <>
                  <button onClick={() => setStatus(p.id, 'posted')} title="Mark as posted" className="p-2 rounded-full transition hover:-translate-y-0.5" style={{ backgroundColor: '#78A18E20', color: '#78A18E' }} data-testid={`plan-mark-posted-${p.id}`}>
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setStatus(p.id, 'skipped')} title="Skip this one" className="p-2 rounded-full transition hover:-translate-y-0.5" style={{ backgroundColor: '#8C867D20', color: '#8C867D' }}>
                    <XCircle className="w-4 h-4" />
                  </button>
                </>
              )}
              <button onClick={() => openEdit(p)} title="Edit" className="p-2 rounded-full transition hover:-translate-y-0.5" style={{ backgroundColor: '#5B7C9920', color: '#5B7C99' }}>
                <Edit3 className="w-4 h-4" />
              </button>
              <button onClick={() => remove(p)} title="Delete" className="p-2 rounded-full transition hover:-translate-y-0.5" style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RhythmDay({ day, tint, title, hint }) {
  return (
    <div className="p-4 rounded-2xl relative overflow-hidden" style={{ backgroundColor: '#FFFFFF', border: `1px solid ${tint}30` }}>
      <p className="text-[10px] uppercase tracking-widest font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: tint, letterSpacing: '0.12em' }}>{day}</p>
      <p className="font-bold mt-0.5 mb-1.5" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: 16 }}>{title}</p>
      <p className="text-xs leading-relaxed" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>{hint}</p>
    </div>
  );
}

